/* assets/gproperty-gateway-route-context.js — GPROPERTY-ROUTE-CONTEXT-09
 * Shared spatial continuity and workspace-state owner for:
 * - GProperty brief → Map opaque Record ID handoff.
 * - Map context chips → canonical URL context + reload/popstate restoration.
 * - Property taxonomy → canonical property_mode URL state + reload/popstate restoration.
 * - Sidebar Scenario [data-sidebar-scenario] .is-active bidirectional mapping.
 * - Context-aware search guidance, verified nearby projection and selected-place next actions.
 * - Map / Properties / GTender / Tools view projection without unmounting the map instance.
 *
 * Requirements: assets/gproperty-flow-context.js remains the sole draft/storage authority.
 * No third-party brand, merchant, store, partner marker or brand-specific referral contract is owned here.
 */
(function gpropertyRouteContext(window, document) {
  'use strict';

  var VERSION = 'gproperty-route-context-v11';
  var STORAGE_KEY = 'gproperty_spatial_brief_draft_v2';
  var LEGACY_STORAGE_KEY = 'gproperty_spatial_brief_draft_v1';

  var GATEWAY_ROOT = '[data-gproperty-page]';
  var MAP_CONFIG = '[data-gproperty-map-config]';
  var MAP_WORKSPACE = '[data-gproperty-map-workspace]';
  var MAP_SEARCH = '[data-gproperty-map-search-root]';
  var MAP_PATH = '/pages/gproperty-map';

  var OWNER_VIEWS = Object.freeze({
    map: Object.freeze({ owner: 'map', workspace: 'map', intent: 'explore_space' }),
    properties: Object.freeze({ owner: 'projects', workspace: 'properties', intent: 'find_property' }),
    gtender: Object.freeze({ owner: 'projects', workspace: 'gtender', intent: 'tender_draft' }),
    tools: Object.freeze({ owner: 'tools', workspace: 'professional', intent: 'professional_tools' })
  });

  var CONTEXTS = Object.freeze({
    find_property: Object.freeze({
      key: 'find_property',
      intent: 'explore',
      nearby: [],
      placeholderZh: '搜尋屋苑、大廈或地址',
      placeholderEn: 'Search estate, building or address',
      guidanceZh: '搜尋屋苑、大廈或地址，查看樓盤與附近環境',
      guidanceEn: 'Search estates, buildings, or addresses to view properties and surroundings'
    }),
    family_fit: Object.freeze({
      key: 'family_fit',
      intent: 'family-fit',
      nearby: ['mtr', 'school', 'park', 'clinic'],
      placeholderZh: '搜尋屋苑或大廈',
      placeholderEn: 'Search an estate or building',
      guidanceZh: '輸入屋苑或大廈，查看家庭生活圈、學校與交通配套',
      guidanceEn: 'Enter an estate or building to view family lifestyle, schools, and transit'
    }),
    owner_fit: Object.freeze({
      key: 'owner_fit',
      intent: 'owner-fit',
      nearby: [],
      placeholderZh: '搜尋物業或地址',
      placeholderEn: 'Search a property or address',
      guidanceZh: '輸入物業或地址，查看出租、出售與資產資料',
      guidanceEn: 'Enter a property or address to view rental, sale, and asset data'
    }),
    more_layers: Object.freeze({
      key: 'more_layers',
      intent: 'layers',
      nearby: [],
      placeholderZh: '搜尋地點、屋苑或地址',
      placeholderEn: 'Search location, estate or address',
      guidanceZh: '搜尋地點、屋苑或地址',
      guidanceEn: 'Search location, estate, or address'
    })
  });

  var INTENT_TO_CONTEXT = Object.freeze({
    explore: 'find_property',
    find_property: 'find_property',
    'find-property': 'find_property',
    'family-fit': 'family_fit',
    'owner-fit': 'owner_fit',
    layers: 'more_layers'
  });

  var PROPERTY_MODES = Object.freeze([
    'buy',
    'rent',
    'owner',
    'new_property',
    'residential',
    'industrial',
    'shop',
    'parking',
    'land',
    'primary_commercial',
    'luxury',
    'gba'
  ]);

  var SUPPORTED_NEARBY = Object.freeze(['mtr', 'school', 'park', 'clinic']);
  var SCENARIO_NEARBY = Object.freeze({
    business_location: Object.freeze(['mtr']),
    home_search: Object.freeze(['mtr', 'school', 'park']),
    planning_change: Object.freeze(['mtr']),
    place_risk: Object.freeze(['mtr', 'school', 'park', 'clinic'])
  });

  if (window.__GPROPERTY_ROUTE_CONTEXT__ === VERSION) return;
  window.__GPROPERTY_ROUTE_CONTEXT__ = VERSION;

  /* ──────────────────────────────────────────────────────────────────────────
   * 1. DOM & TEXT UTILITIES
   * ────────────────────────────────────────────────────────────────────────── */
  function one(root, selector) {
    return (root || document).querySelector(selector);
  }

  function all(root, selector) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function clean(value) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
  }

  function opaque(value) {
    var text = clean(value);
    return /^[A-Za-z0-9._:-]{1,160}$/.test(text) ? text : '';
  }

  function isZh() {
    return clean(document.documentElement.lang).toLowerCase().indexOf('zh') === 0;
  }

  function isProAccess(workspace) {
    return !!workspace && clean(workspace.getAttribute('data-access-tier')).toLowerCase() === 'pro';
  }

  function isProCapability(node) {
    return !!(node && node.hasAttribute && node.hasAttribute('data-pro-capability'));
  }

  function projectCapabilityState(workspace) {
    var pro = isProAccess(workspace);
    all(workspace, '[data-pro-capability]').forEach(function (node) {
      node.classList.toggle('is-pro-locked', !pro);
      node.setAttribute('data-pro-state', pro ? 'enabled' : 'locked');
      if (node.tagName === 'BUTTON') {
        node.disabled = !pro;
        node.setAttribute('aria-disabled', pro ? 'false' : 'true');
        if (pro) node.removeAttribute('tabindex');
        else node.setAttribute('tabindex', '-1');
      } else if (!pro) {
        node.setAttribute('aria-disabled', 'true');
        node.setAttribute('tabindex', '-1');
      } else {
        node.setAttribute('aria-disabled', 'false');
        node.removeAttribute('tabindex');
      }
    });
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * 2. DATA GATE DRAFT INTERFACE
   * ────────────────────────────────────────────────────────────────────────── */
  function dataGate() {
    return window.GPROPERTY_DATA_GATE || null;
  }

  function readDraft(key) {
    var gate = dataGate();
    if (!gate || typeof gate.readPropertyDraft !== 'function') return null;
    var stored = gate.readPropertyDraft(key || STORAGE_KEY, gate.legacyDraftKey || LEGACY_STORAGE_KEY);
    return stored && stored.draft ? stored.draft : null;
  }

  function recordId(draft) {
    var gate = dataGate();
    return gate && typeof gate.opaque === 'function'
      ? gate.opaque(draft && draft.record_id)
      : opaque(draft && draft.record_id);
  }

  function contextualUrl(base, draft) {
    let url;

    try {
      url = new URL(base, window.location.origin);
    } catch (_) {
      return base;
    }

    const id = recordId(draft);

    url.searchParams.delete('record_id');

    if (id) {
      url.searchParams.set('record_id', id);
    }

    return url.pathname + url.search + url.hash;
  }

  function decorateGateway(root) {
    var draft = readDraft(STORAGE_KEY);
    var id = recordId(draft);
    all(root, '[data-gproperty-route-card]').forEach(function (link) {
      var raw = link.getAttribute('data-gproperty-base-href') || link.getAttribute('href') || '';
      if (!link.hasAttribute('data-gproperty-base-href')) link.setAttribute('data-gproperty-base-href', raw);
      var url;
      try {
        url = new URL(raw, window.location.origin);
      } catch (_) {
        return;
      }
      if (url.origin !== window.location.origin || url.pathname !== MAP_PATH) return;
      url.searchParams.delete('record_id');
      if (id) url.searchParams.set('record_id', id);
      link.href = url.pathname + url.search + url.hash;
      link.setAttribute('data-gproperty-context-state', id ? 'attached' : 'empty');
    });
    root.setAttribute('data-route-context-version', VERSION);
    root.setAttribute('data-route-context-state', id ? 'attached' : 'empty');
  }

  function mountGateway(root) {
    if (!root || root.getAttribute('data-route-context-bound') === VERSION) return;
    root.setAttribute('data-route-context-bound', VERSION);
    decorateGateway(root);
    var form = one(root, '[data-gproperty-form]');
    if (form) {
      form.addEventListener('submit', function () {
        window.setTimeout(function () {
          decorateGateway(root);
        }, 0);
      });
    }
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * 3. ROUTE MAPS & UI CONTEXTS
   * ────────────────────────────────────────────────────────────────────────── */
  function mapRoutes(config) {
    return {
      'family-fit': {
        key: 'family-fit',
        href: clean(config && config.dataset.familyFitUrl) || '/pages/gproperty-family-fit?stage=detail&source=gproperty-map&intent=family-fit'
      },
      'owner-fit': {
        key: 'owner-fit',
        href: clean(config && config.dataset.ownerFitUrl) || '/pages/gproperty-owner?stage=detail&source=gproperty-map&intent=owner-fit'
      },
      assets: {
        key: 'assets',
        href: clean(config && config.dataset.assetsUrl) || '/pages/gproperty-assets?stage=detail&source=gproperty-map&intent=evidence-readiness'
      },
      evidence: {
        key: 'evidence',
        href: clean(config && config.dataset.reportUrl) || '/pages/gproperty-report?stage=insights&source=gproperty-map&intent=evidence-preview'
      }
    };
  }

  function preferredRoute(draft) {
    var gate = dataGate();
    if (!gate || typeof gate.isVerifiedDraft !== 'function' || !gate.isVerifiedDraft(draft)) return 'assets';
    var need = clean(draft && draft.business_need);
    var asset = clean(draft && draft.asset_type);
    if (need === 'valuation-route' || need === 'asset-activation') return 'owner-fit';
    if (need === 'survey-route' || need === 'renewal-route') return 'assets';
    if (asset === 'residential-estate' || asset === 'village-rural') return 'family-fit';
    if (need === 'public-data-context' || need === 'property-snapshot') return 'family-fit';
    return 'assets';
  }

  function currentContextFromLocation() {
    var url;
    try {
      url = new URL(window.location.href);
    } catch (_) {
      return 'find_property';
    }
    var context = clean(url.searchParams.get('context')).toLowerCase();

    const mapConfig = one(document, MAP_CONFIG);
    const isPlaceContext =
      mapConfig &&
      mapConfig.getAttribute('data-route-role') === 'place-context';

    if (isPlaceContext) {
      return context === 'more_layers'
        ? 'more_layers'
        : 'find_property';
    }

    if (Object.prototype.hasOwnProperty.call(CONTEXTS, context)) return context;
    var legacyIntent = clean(url.searchParams.get('intent')).toLowerCase();
    return INTENT_TO_CONTEXT[legacyIntent] || 'find_property';
  }

  function scenarioFromLocation() {
    var url;
    try {
      url = new URL(window.location.href);
    } catch (_) {
      return { mode: '', nearby: [] };
    }
    var mode = clean(url.searchParams.get('surface_mode') || url.searchParams.get('mode')).toLowerCase();
    if (!Object.prototype.hasOwnProperty.call(SCENARIO_NEARBY, mode)) mode = '';
    var raw = clean(url.searchParams.get('nearby') || url.searchParams.get('context_layers') || url.searchParams.get('layers'));
    var nearby = raw
      ? raw.split(',').map(function (item) {
          return clean(item).toLowerCase();
        }).filter(function (item, index, items) {
          return SUPPORTED_NEARBY.indexOf(item) !== -1 && items.indexOf(item) === index;
        })
      : [];
    if (!nearby.length && mode) nearby = SCENARIO_NEARBY[mode].slice();
    return { mode: mode, nearby: nearby };
  }

  /* Spec v2.0 Sync: Maps URL surface_mode to [data-sidebar-scenario] .is-active */
  function syncSidebarScenarioState(surfaceMode) {
    var currentMode = surfaceMode != null ? clean(surfaceMode).toLowerCase() : scenarioFromLocation().mode;
    all(document, '[data-sidebar-scenario]').forEach(function (button) {
      var scenario = clean(button.getAttribute('data-sidebar-scenario')).toLowerCase();
      var active = !!(currentMode && scenario === currentMode);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function normalizePropertyMode(value) {
    var mode = clean(value).toLowerCase();
    return PROPERTY_MODES.indexOf(mode) !== -1 ? mode : '';
  }

  function propertyModeFromLocation() {
    var url;
    try {
      url = new URL(window.location.href);
    } catch (_) {
      return '';
    }
    return normalizePropertyMode(url.searchParams.get('property_mode'));
  }

  function writePropertyModeState(propertyMode, historyMode) {
    var mode = normalizePropertyMode(propertyMode);
    var url;
    try {
      url = new URL(window.location.href);
    } catch (_) {
      return;
    }
    var rawCurrent = clean(url.searchParams.get('property_mode')).toLowerCase();
    if (mode) url.searchParams.set('property_mode', mode);
    else url.searchParams.delete('property_mode');
    if (rawCurrent === mode) return;
    var nextUrl = url.pathname + url.search + url.hash;
    if (historyMode === 'push') window.history.pushState(window.history.state || {}, '', nextUrl);
    else window.history.replaceState(window.history.state || {}, '', nextUrl);
  }

  function projectPropertyMode(workspace, propertyMode, options) {
    if (!workspace) return '';
    var opts = options || {};
    var mode = normalizePropertyMode(propertyMode);
    if (mode) workspace.setAttribute('data-property-mode', mode);
    else workspace.removeAttribute('data-property-mode');

    all(workspace, '[data-gproperty-map-intent]').forEach(function (button) {
      var buttonMode = normalizePropertyMode(button.getAttribute('data-gproperty-map-intent'));
      var active = !!(mode && buttonMode === mode);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (opts.syncUrl) writePropertyModeState(mode, opts.historyMode || 'replace');
    if (opts.emit !== false) {
      window.dispatchEvent(new CustomEvent('gproperty:property-mode-change', {
        detail: {
          propertyMode: mode,
          source: opts.source || 'gproperty-map',
          context: clean(workspace.getAttribute('data-active-context')) || currentContextFromLocation()
        }
      }));
    }
    return mode;
  }

  function removeLegacyContextIntent(url) {
    var legacyIntent = clean(url && url.searchParams && url.searchParams.get('intent')).toLowerCase();
    if (legacyIntent && Object.prototype.hasOwnProperty.call(INTENT_TO_CONTEXT, legacyIntent)) url.searchParams.delete('intent');
  }

  function writeContextState(contextKey) {
    var context = CONTEXTS[contextKey] || CONTEXTS.find_property;
    var url;
    try {
      url = new URL(window.location.href);
    } catch (_) {
      return;
    }
    url.searchParams.set('context', context.key);
    removeLegacyContextIntent(url);
    window.history.replaceState(window.history.state || {}, '', url.pathname + url.search + url.hash);
  }

  function setContextChipState(workspace, contextKey) {
    all(workspace, '[data-gproperty-map-context]').forEach(function (button) {
      var active = clean(button.getAttribute('data-gproperty-map-context')) === contextKey;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function setPropertyControls(workspace, visible) {
    var controls = one(workspace, '[data-gproperty-map-property-controls]');
    if (controls) controls.hidden = !visible;
  }

  function setSearchCopy(contextKey) {
    var context = CONTEXTS[contextKey] || CONTEXTS.find_property;
    var searchRoot = one(document, MAP_SEARCH);
    var input = one(searchRoot, '[data-gproperty-map-search-input]');
    var status = one(searchRoot, '[data-gproperty-map-search-status-text]');
    const mapConfig = one(document, MAP_CONFIG);
    const isPlaceContext =
      mapConfig &&
      mapConfig.getAttribute('data-route-role') === 'place-context';

    const placeholder = isPlaceContext
      ? (isZh()
          ? '搜尋地點、大廈或地址'
          : 'Search a place, building or address')
      : (isZh() ? context.placeholderZh : context.placeholderEn);

    const guidance = isPlaceContext
      ? (isZh()
          ? '搜尋並選擇地點，開始探索周邊'
          : 'Search and select a location to explore its surroundings')
      : (isZh()
          ? (context.guidanceZh || placeholder)
          : (context.guidanceEn || placeholder));
    if (input) {
      input.placeholder = placeholder;
      input.setAttribute('aria-label', guidance);
    }
    if (status && (!input || !clean(input.value))) status.textContent = guidance;
  }

  function scenarioGuidance(nearby) {
    var labels = {
      mtr: isZh() ? '港鐵' : 'MTR',
      school: isZh() ? '學校' : 'schools',
      park: isZh() ? '公園' : 'parks',
      clinic: isZh() ? '醫療' : 'medical'
    };
    var items = nearby.map(function (key) {
      return labels[key] || key;
    });
    if (!items.length) return isZh() ? '請先搜尋並選擇一個地點。' : 'Search and select a location first.';
    return isZh()
      ? '選定地點後會優先顯示：' + items.join('、') + '。'
      : 'After you select a location, priority amenities: ' + items.join(', ') + '.';
  }

  function nearbyForContext(contextKey) {
    var context = CONTEXTS[contextKey] || CONTEXTS.find_property;
    if (context.nearby.length) return context.nearby.slice();
    return scenarioFromLocation().nearby;
  }

  function markNearbyContext(workspace, nearby) {
    workspace.setAttribute('data-scenario-nearby', nearby.join(','));
    all(workspace, '[data-gproperty-map-nearby-type]').forEach(function (button) {
      var type = clean(button.getAttribute('data-gproperty-map-nearby-type')).toLowerCase();
      if (nearby.indexOf(type) !== -1) button.setAttribute('data-scenario-suggested', 'true');
      else button.removeAttribute('data-scenario-suggested');
    });
  }

  function syncNearbyForContext(workspace, nearby) {
    if (
      !workspace ||
      !workspace.isConnected ||
      !clean(workspace.getAttribute('data-selected-estate'))
    ) {
      return;
    }

    const desired = new Set(nearby);

    all(workspace, '[data-gproperty-map-nearby-type]').forEach(function (button) {
      const type = clean(
        button.getAttribute('data-gproperty-map-nearby-type')
      ).toLowerCase();

      if (!type) return;

      const active = button.getAttribute('aria-pressed') === 'true';

      if (active && !desired.has(type)) {
        button.click();
        return;
      }

      if (
        !desired.has(type) ||
        active ||
        button.disabled ||
        button.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }

      if (
        button.getAttribute('data-scenario-autoload-pending') === 'true'
      ) {
        return;
      }

      button.setAttribute('data-scenario-autoload-pending', 'true');

      window.setTimeout(function () {
        button.removeAttribute('data-scenario-autoload-pending');

        if (
          !workspace.isConnected ||
          !button.isConnected ||
          !workspace.contains(button) ||
          !clean(workspace.getAttribute('data-selected-estate')) ||
          button.disabled ||
          button.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        const currentDesired = clean(
          workspace.getAttribute('data-scenario-nearby')
        ).split(',').map(function (value) {
          return value.trim().toLowerCase();
        }).filter(Boolean);

        if (!currentDesired.includes(type)) return;

        if (button.getAttribute('aria-pressed') !== 'true') {
          button.click();
        }
      }, 0);
    });
  }

  function openLayerPanel(workspace) {
    var trigger = one(workspace, '[data-gproperty-map-layer-trigger]');
    var panel = one(workspace, '[data-gproperty-map-layer-panel]');
    if (!trigger || !panel || !panel.hidden) return;
    trigger.click();
  }

  function closeLayerPanel(workspace) {
    var trigger = one(workspace, '[data-gproperty-map-layer-trigger]');
    var panel = one(workspace, '[data-gproperty-map-layer-panel]');
    if (!trigger || !panel || panel.hidden) return;
    trigger.click();
  }

  function syncLayerThumbState(workspace) {
    if (!workspace) return;
    var activeTypes = all(workspace, '[data-gproperty-map-nearby-type][aria-pressed="true"]').map(function (button) {
      return clean(button.getAttribute('data-gproperty-map-nearby-type')).toLowerCase();
    }).filter(Boolean);
    all(workspace, '[data-gproperty-map-layer-id]').forEach(function (button) {
      var type = clean(button.getAttribute('data-gproperty-map-layer-nearby-type')).toLowerCase();
      var base = clean(button.getAttribute('data-gproperty-map-layer-id')).toLowerCase() === 'base';
      var active = base || (!!type && activeTypes.indexOf(type) !== -1);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function resetLayerContext(config, workspace) {
    if (!config || !workspace || clean(workspace.getAttribute('data-active-context')) !== 'more_layers') return;
    var url;
    try {
      url = new URL(window.location.href);
      url.searchParams.set('context', CONTEXTS.find_property.key);
      ['surface_mode', 'context_layers', 'layers', 'nearby'].forEach(function (key) {
        url.searchParams.delete(key);
      });
      removeLegacyContextIntent(url);
      window.history.replaceState(window.history.state || {}, '', url.pathname + url.search + url.hash);
    } catch (_) {}
    applyActiveContext(config, workspace, 'find_property', {
      syncUrl: false,
      autoload: false,
      openLayers: false,
      propertyControls: false
    });
    syncLayerThumbState(workspace);
  }

  function bindLayerContext(config, workspace) {
    if (!config || !workspace || workspace.getAttribute('data-layer-context-bound') === VERSION) return;
    workspace.setAttribute('data-layer-context-bound', VERSION);
    var panel = one(workspace, '[data-gproperty-map-layer-panel]');
    var list = one(workspace, '[data-gproperty-map-layer-list]');
    var proxies = one(workspace, '[data-gproperty-map-nearby-runtime-proxies]');
    var scheduled = false;

    function scheduleSync() {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        syncLayerThumbState(workspace);
      });
    }

    if (panel) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (mutation.attributeName === 'hidden' && panel.hidden) resetLayerContext(config, workspace);
        });
      }).observe(panel, { attributes: true, attributeFilter: ['hidden'] });
    }
    if (list) new MutationObserver(scheduleSync).observe(list, { childList: true });
    if (proxies) new MutationObserver(scheduleSync).observe(proxies, { attributes: true, subtree: true, attributeFilter: ['aria-pressed'] });
    scheduleSync();
  }

  function updateContextActions(config, workspace, contextKey) {
    var container = one(workspace, '[data-gproperty-map-context-actions]');
    if (!container) return;
    var selected = !!clean(workspace.getAttribute('data-selected-estate'));
    var routes = mapRoutes(config);
    var draft = readDraft(STORAGE_KEY);
    var order = contextKey === 'family_fit'
      ? ['family-fit', 'evidence']
      : (contextKey === 'owner_fit' ? ['owner-fit', 'assets'] : (contextKey === 'find_property' ? ['evidence', 'assets'] : []));

    all(container, '[data-gproperty-map-context-action]').forEach(function (link) {
      var key = clean(link.getAttribute('data-gproperty-map-context-action'));
      var item = routes[key];
      var index = order.indexOf(key);
      link.hidden = !selected || index === -1 || !item;
      link.classList.toggle('gproperty-map-context-cta--primary', index === 0);
      link.classList.toggle('gproperty-map-context-cta--secondary', index === 1);
      if (item && isProAccess(workspace)) {
        link.href = contextualUrl(item.href, draft);
        link.setAttribute('aria-disabled', 'false');
        link.removeAttribute('tabindex');
      } else {
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
      }
    });
    container.hidden = !selected || !order.length;
  }

  function updateSourceNotice(workspace) {
    if (!workspace) return;

    const notice = one(workspace, '[data-gproperty-map-source-notice]');
    if (!notice) return;

    const sourceNode = one(workspace, '[data-gproperty-map-selected-source]');
    const source = clean(sourceNode && sourceNode.textContent);
    const selected = Boolean(
      clean(workspace.getAttribute('data-selected-estate'))
    );

    const unavailable =
      !source ||
      /^(?:—|-|未返回|未提供|未知|沒有返回|unknown|not returned|not provided|n\/a)$/i.test(source);

    notice.hidden = !selected || unavailable;
  }

  function normalizeNearbyPresentation(workspace) {
    if (!workspace) return;
    var selected = !!clean(workspace.getAttribute('data-selected-estate'));
    var status = one(workspace, '[data-gproperty-map-nearby-status]');
    if (status) {
      var text = clean(status.textContent);
      var count = text.match(/^(\d+)\s*項$/);
      var next = text;
      if (!selected) {
        next = isZh()
          ? '在地圖上選擇地點，開始探索周邊'
          : 'Select a location to explore surroundings';
      } else if (count) {
        next = isZh()
          ? count[1] + ' 個已找到'
          : count[1] + ' found';
      } else if (/尚未配置/.test(text)) {
        next = isZh()
          ? '此類別暫未提供，請先探索其他設施'
          : 'This category is currently unavailable';
      } else if (text === '載入中…') {
        next = isZh()
          ? '正在為您查找周邊資訊…'
          : 'Finding nearby information…';
      } else if (
        text === '選擇周邊配套後載入' ||
        text === '選擇地點後載入'
      ) {
        next = isZh()
          ? '請選擇想查看的周邊設施或圖層'
          : 'Select a category to explore nearby amenities';
      } else if (text === '暫無已核實配套資料') {
        next = isZh()
          ? '未找到相關資訊，請試試其他類別或地點'
          : 'No matching information found';
      } else if (text === '部分配套資料未能載入') {
        next = isZh()
          ? '部分周邊資訊暫未能提供，請稍後再試'
          : 'Some nearby information is unavailable. Please try again later';
      } else if (
        text === '配套資料來源尚未就緒' ||
        /尚未可用/.test(text)
      ) {
        next = isZh()
          ? '周邊資訊暫未能提供，請稍後再試'
          : 'Nearby information is temporarily unavailable. Please try again later';
      } else if (text === '無權限載入配套資料') {
        next = isZh()
          ? '暫時無法查看此內容，請確認登入狀態後再試'
          : 'Check that you are signed in and try again';
      } else if (text === '暫時未能載入配套資料') {
        next = isZh()
          ? '暫時無法取得周邊資訊，請稍後再試'
          : 'Unable to retrieve nearby information. Please try again later';
      } else if (text === '此圖層僅在 Pro 版本中提供') {
        next = isZh()
          ? '此進階圖層供 Pro 用戶使用'
          : 'This advanced layer is available to Pro users';
      }
      if (status.textContent !== next) status.textContent = next;
    }

    all(workspace, '[data-gproperty-map-nearby-list] li').forEach(function (item) {
      var text = clean(item.textContent);
      var match = text.match(/\s·\s(\d+(?:\.\d+)?)\s*m(?=\s·|$)/i);
      if (!match) return;
      var metres = Number(match[1]);
      if (!Number.isFinite(metres)) return;
      var value;
      if (metres >= 1000) {
        var km = (Math.round(metres / 100) / 10).toFixed(1).replace(/\.0$/, '');
        value = isZh() ? '約 ' + km + ' 公里' : 'Approx. ' + km + ' km';
      } else {
        var rounded = String(Math.round(metres));
        value = isZh() ? '約 ' + rounded + ' 米' : 'Approx. ' + rounded + ' m';
      }
      var nextText = text.replace(match[0], ' · ' + value);
      if (item.textContent !== nextText) item.textContent = nextText;
    });
  }

  function applyActiveContext(config, workspace, contextKey, options) {
    if (!workspace) return;
    var key = CONTEXTS[contextKey] ? contextKey : 'find_property';
    var opts = options || {};
    var scenarioInfo = scenarioFromLocation();
    var nearby = nearbyForContext(key);
    workspace.setAttribute('data-active-context', key);
    setContextChipState(workspace, key);
    syncSidebarScenarioState(scenarioInfo.mode);
    setSearchCopy(key);
    markNearbyContext(workspace, nearby);
    if (key !== 'find_property') setPropertyControls(workspace, false);
    else if (opts.propertyControls != null) setPropertyControls(workspace, !!opts.propertyControls);
    if (opts.syncUrl) writeContextState(key);
    if (opts.autoload && key !== 'more_layers') syncNearbyForContext(workspace, nearby);
    if (key === 'more_layers' && opts.openLayers) openLayerPanel(workspace);
    else if (key !== 'more_layers') closeLayerPanel(workspace);
    updateContextActions(config, workspace, key);
    updateSourceNotice(workspace);
    normalizeNearbyPresentation(workspace);
    syncLayerThumbState(workspace);

    if (!clean(workspace.getAttribute('data-selected-estate'))) {
      var mapStatus = one(workspace, '[data-gproperty-map-status]');
      if (mapStatus) mapStatus.textContent = scenarioGuidance(nearby);
    }
  }

  function updateMapHandoff(config, workspace) {
    var handoff = one(workspace, '[data-gproperty-map-handoff]');
    if (!handoff) return;
    var primary = one(handoff, '[data-gproperty-map-primary-action]');
    var label = one(primary, '[data-gproperty-map-primary-label]');
    var key = clean(config && config.dataset.localDraftKey) || STORAGE_KEY;
    var draft = readDraft(key);
    var routes = mapRoutes(config);
    var preferred = preferredRoute(draft);
    var route = routes[preferred] || routes['family-fit'];
    var selected = !!clean(workspace.getAttribute('data-selected-estate'));

    handoff.setAttribute('data-primary-route', route.key);
    if (primary) primary.setAttribute('data-gproperty-map-primary-route', route.key);
    all(handoff, '[data-gproperty-map-route]').forEach(function (link) {
      var routeKey = clean(link.getAttribute('data-gproperty-map-route'));
      var item = routes[routeKey];
      link.hidden = true;
      if (item && isProAccess(workspace)) {
        link.href = contextualUrl(item.href, draft);
        link.setAttribute('aria-disabled', 'false');
        link.removeAttribute('tabindex');
      } else {
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
      }
    });
    if (!primary || !label) return;
    if (!selected) {
      primary.removeAttribute('href');
      primary.setAttribute('aria-disabled', 'true');
      label.textContent = isZh() ? '請先在地圖上選擇或搜尋目標地點' : 'Select or search a target location first';
      return;
    }
    if (!isProAccess(workspace)) {
      primary.removeAttribute('href');
      primary.setAttribute('aria-disabled', 'true');
      primary.setAttribute('tabindex', '-1');
      label.textContent = isZh() ? '專業分析 · Pro' : 'Professional analysis · Pro';
      return;
    }
    primary.href = contextualUrl(route.href, draft);
    primary.setAttribute('aria-disabled', 'false');
    primary.removeAttribute('tabindex');
  }

  function focusMapSearch() {
    var input = one(one(document, MAP_SEARCH), '[data-gproperty-map-search-input]');
    if (input) input.focus();
  }

  function updateMapGuidance(workspace) {
    if (!workspace) return;
    var note = one(workspace, '[data-gproperty-map-location-guidance]');
    if (!note) return;
    if (!window.navigator.onLine) {
      note.textContent = isZh() ? '目前離線；定位與真實地點搜尋暫不可用。' : 'You are offline; location and live place search are temporarily unavailable.';
      return;
    }
    note.textContent = isZh() ? '定位為可選功能；不開啟也不影響搜尋地點與查看地圖。' : 'Location is optional. You can search and use the map without enabling it.';
  }

  function ownerCopy(view) {
    var zh = isZh();
    var copy = {
      map: { title: '地圖瀏覽 · Map View', zh: '自由查看周邊環境、交通與配套，快速了解這個地方。', en: 'Explore the map freely to check local transportation and amenities.' },
      properties: { title: '找樓盤 · Find Properties', zh: '在地圖上查看樓盤與附近環境。', en: 'View properties and surroundings on the same map.' },
      gtender: { title: '找服務與協助 · Services & Help', zh: '提交需求並尋找專業服務。', en: 'Submit a request and find professional services.' },
      tools: { title: '進階地圖工具 · Advanced Tools', zh: '切換至更深入的空間工具。', en: 'Switch to advanced spatial tools.' }
    };
    var item = copy[view] || copy.map;
    return { title: item.title, description: zh ? item.zh : item.en };
  }

  function viewFromLocation() {
    var url;
    try {
      url = new URL(window.location.href);
    } catch (_) {
      return 'map';
    }
    var workspace = clean(url.searchParams.get('workspace')).toLowerCase();
    var owner = clean(url.searchParams.get('owner')).toLowerCase();
    if (workspace === 'map' || owner === 'map') return 'map';
    if (workspace === 'gtender') return 'gtender';
    if (workspace === 'professional' || owner === 'tools') return 'tools';
    return 'map';
  }

  function renderOwnerView(workspace, view) {
    if (!workspace) return;
    var resolved = OWNER_VIEWS[view] ? view : 'map';
    if (resolved !== 'map' && !isProAccess(workspace)) resolved = 'map';
    var copy = ownerCopy(resolved);
    workspace.setAttribute('data-active-owner-view', resolved);
    workspace.setAttribute('data-active-shell-owner', OWNER_VIEWS[resolved].owner);
    workspace.setAttribute('data-active-workspace', OWNER_VIEWS[resolved].workspace);
    all(workspace, '[data-gproperty-map-owner-view]').forEach(function (button) {
      var active = clean(button.getAttribute('data-gproperty-map-owner-view')) === resolved;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    var title = one(workspace, '[data-gproperty-map-owner-title]');
    var description = one(workspace, '[data-gproperty-map-owner-description]');
    if (title) title.textContent = copy.title;
    if (description) description.textContent = copy.description;
  }

  function mapContext(workspace, view) {
    var map = workspace && workspace.__map;
    var center = map && typeof map.getCenter === 'function' ? map.getCenter() : null;
    var bounds = map && typeof map.getBounds === 'function' ? map.getBounds() : null;
    var bbox = '';
    if (bounds && typeof bounds.getWest === 'function') {
      bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()].map(function (value) {
        return Number(value).toFixed(6);
      }).join(',');
    }
    // Legacy map selection reference, not proof of canonical Place identity.
    const selectedId = clean(
      workspace && workspace.getAttribute('data-selected-estate')
    );

    const nameNode = workspace
      ? one(workspace, '[data-gproperty-map-selected-name]')
      : null;

    const addrNode = workspace
      ? one(workspace, '[data-gproperty-map-selected-address]')
      : null;

    const selectedName = selectedId
      ? clean(nameNode && nameNode.textContent)
      : '';

    const selectedAddress = selectedId
      ? clean(addrNode && addrNode.textContent)
      : '';
    var viewConfig = OWNER_VIEWS[view] || OWNER_VIEWS.map;
    return {
      placeId: selectedId,
      placeLabel: selectedName || selectedAddress,
      radius: 500,
      intent: viewConfig.intent,
      propertyMode: normalizePropertyMode(workspace && workspace.getAttribute('data-property-mode')),
      bbox: bbox,
      selectedBuilding: selectedId,
      camera: center ? { lat: center.lat, lng: center.lng, zoom: typeof map.getZoom === 'function' ? map.getZoom() : 15 } : null
    };
  }

  function activateOwnerView(workspace, view, historyMode) {
    var config = OWNER_VIEWS[view];
    if (!workspace || !config) return false;
    if (view !== 'map' && !isProAccess(workspace)) return false;
    renderOwnerView(workspace, view);
    document.dispatchEvent(new CustomEvent('littleg:workspace-owner-change', {
      detail: {
        owner: config.owner,
        workspace: config.workspace,
        intent: config.intent,
        context: mapContext(workspace, view),
        history: historyMode || 'replace',
        source: 'gproperty-map'
      }
    }));
    return true;
  }

  function bindOwnerViews(workspace) {
    all(workspace, '[data-gproperty-map-owner-view]').forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        if (isProCapability(button) && !isProAccess(workspace)) return;
        activateOwnerView(workspace, clean(button.getAttribute('data-gproperty-map-owner-view')), 'push');
      });
    });
    renderOwnerView(workspace, viewFromLocation());
  }

  function bindContextChips(config, workspace) {
    all(workspace, '[data-gproperty-map-context]').forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        var key = clean(button.getAttribute('data-gproperty-map-context'));
        if (!CONTEXTS[key]) return;
        var current = clean(workspace.getAttribute('data-active-context')) || 'find_property';
        var propertyControls = one(workspace, '[data-gproperty-map-property-controls]');
        var showPropertyControls = key === 'find_property' && (current !== 'find_property' || !propertyControls || propertyControls.hidden);
        applyActiveContext(config, workspace, key, {
          syncUrl: true,
          autoload: true,
          openLayers: key === 'more_layers',
          propertyControls: showPropertyControls
        });
      });
    });

    all(workspace, '[data-gproperty-map-intent]').forEach(function (button) {
      button.addEventListener('click', function () {
        var mode = normalizePropertyMode(button.getAttribute('data-gproperty-map-intent'));
        if (!mode) return;
        var current = normalizePropertyMode(workspace.getAttribute('data-property-mode'));
        projectPropertyMode(workspace, current === mode ? '' : mode, {
          syncUrl: true,
          historyMode: 'push',
          source: 'property-taxonomy'
        });
      });
    });

    var reset = one(workspace, '[data-gproperty-map-reset-filters]');
    if (reset) {
      reset.addEventListener('click', function () {
        projectPropertyMode(workspace, '', {
          syncUrl: true,
          historyMode: 'push',
          source: 'property-filter-reset'
        });
      });
    }
  }

  function bindNearbyPresentation(workspace) {
    var root = one(workspace, '[data-gproperty-map-nearby]');
    if (!root || root.getAttribute('data-context-presentation-bound') === VERSION) return;
    root.setAttribute('data-context-presentation-bound', VERSION);
    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        normalizeNearbyPresentation(workspace);
      });
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
  }

  function mountMap(config, workspace) {
    if (!config || !workspace || workspace.getAttribute('data-route-context-bound') === VERSION) return;
    workspace.setAttribute('data-route-context-bound', VERSION);

    var primary = one(workspace, '[data-gproperty-map-primary-action]');
    if (primary) {
      primary.addEventListener('click', function (event) {
        if (primary.getAttribute('aria-disabled') !== 'true') return;
        event.preventDefault();
        if (isProCapability(primary) && !isProAccess(workspace)) return;
        focusMapSearch();
      });
    }

    var locationButton = one(workspace, '[data-gproperty-map-use-location]');
    var locationNote = one(workspace, '[data-gproperty-map-location-guidance]');
    if (locationButton) {
      locationButton.addEventListener('click', function () {
        if (!locationNote) return;
        locationNote.textContent = isZh()
          ? '請在系統提示中選擇是否允許存取位置；拒絕後仍可使用上方搜尋。'
          : 'Choose whether to allow location in the system prompt. Manual search remains available.';
      });
    }

    var restoredPropertyMode = propertyModeFromLocation();
    projectCapabilityState(workspace);
    bindOwnerViews(workspace);
    bindContextChips(config, workspace);
    projectPropertyMode(workspace, restoredPropertyMode, { syncUrl: false, emit: false, source: 'initial-url' });
    bindNearbyPresentation(workspace);
    bindLayerContext(config, workspace);
    updateMapHandoff(config, workspace);
    updateMapGuidance(workspace);
    syncSidebarScenarioState();
    applyActiveContext(config, workspace, currentContextFromLocation(), {
      autoload: false,
      propertyControls: !!restoredPropertyMode && currentContextFromLocation() === 'find_property',
      openLayers: false
    });

    var observer = new MutationObserver(function (mutations) {
      if (!mutations.some(function (item) { return item.attributeName === 'data-selected-estate'; })) return;
      updateMapHandoff(config, workspace);
      applyActiveContext(config, workspace, clean(workspace.getAttribute('data-active-context')) || currentContextFromLocation(), { autoload: true });
      renderOwnerView(workspace, clean(workspace.getAttribute('data-active-owner-view')) || viewFromLocation());
    });
    observer.observe(workspace, { attributes: true, attributeFilter: ['data-selected-estate'] });
  }

  function boot(scope) {
    var target = scope || document;
    if (target.matches && target.matches(GATEWAY_ROOT)) mountGateway(target);
    all(target, GATEWAY_ROOT).forEach(mountGateway);
    syncSidebarScenarioState();
    mountMap(one(document, MAP_CONFIG), one(document, MAP_WORKSPACE));
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * 4. LIFECYCLE & EVENT LISTENERS
   * ────────────────────────────────────────────────────────────────────────── */
  window.addEventListener('gproperty:estate:selected', function () {
    var workspace = one(document, MAP_WORKSPACE);
    var config = one(document, MAP_CONFIG);
    if (!config || !workspace) return;
    updateMapHandoff(config, workspace);
    applyActiveContext(config, workspace, clean(workspace.getAttribute('data-active-context')) || currentContextFromLocation(), { autoload: true });
    syncLayerThumbState(workspace);
  });

  window.addEventListener('popstate', function () {
    var workspace = one(document, MAP_WORKSPACE);
    var config = one(document, MAP_CONFIG);
    syncSidebarScenarioState();
    if (!workspace) return;
    var propertyMode = propertyModeFromLocation();
    projectPropertyMode(workspace, propertyMode, { syncUrl: false, source: 'history' });
    renderOwnerView(workspace, viewFromLocation());
    if (config) {
      var contextKey = currentContextFromLocation();
      applyActiveContext(config, workspace, contextKey, {
        autoload: false,
        propertyControls: !!propertyMode && contextKey === 'find_property',
        openLayers: false
      });
    }
  });

  window.addEventListener('storage', function (event) {
    if (event && event.key !== STORAGE_KEY && event.key !== LEGACY_STORAGE_KEY) return;
    all(document, GATEWAY_ROOT).forEach(decorateGateway);
    var config = one(document, MAP_CONFIG);
    var workspace = one(document, MAP_WORKSPACE);
    if (config && workspace) {
      updateMapHandoff(config, workspace);
      updateContextActions(config, workspace, clean(workspace.getAttribute('data-active-context')) || currentContextFromLocation());
    }
  });

  window.addEventListener('online', function () {
    var workspace = one(document, MAP_WORKSPACE);
    if (workspace) updateMapGuidance(workspace);
  });

  window.addEventListener('offline', function () {
    var workspace = one(document, MAP_WORKSPACE);
    if (workspace) updateMapGuidance(workspace);
  });

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    }, { once: true });
  } else {
    boot(document);
  }
})(window, document);